<template>
  <div
    v-if="active"
    class="ptr-indicator"
    :class="{ 'ptr-refreshing': refreshing }"
    :style="indicatorStyle"
  >
    <div class="ptr-spinner" :style="spinnerStyle">
      <svg viewBox="0 0 24 24" width="22" height="22">
        <path
          d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
          fill="currentColor"
        />
      </svg>
    </div>
  </div>
</template>

<script>
// Pull-to-refresh for standalone iOS PWAs (which strip the browser's
// built-in gesture). On Android Chrome the browser provides its own
// pull-to-refresh, so we disable this component there to avoid a
// double-gesture. The hook is a full page reload — same as swiping
// down in a browser.

const TRIGGER_DISTANCE = 70;   // px the finger must travel to arm the refresh
const MAX_PULL = 120;          // clamp visual pull so the indicator doesn't run off

function isStandalonePwa() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true; // iOS Safari legacy
}

export default {
  name: 'PullToRefresh',
  data() {
    return {
      enabled: isStandalonePwa(),
      active: false,       // true while finger is down and we're tracking
      pull: 0,             // current pull distance in px
      refreshing: false,   // true once trigger fires, until reload
      startY: 0,
      startScrollable: null,
    };
  },
  computed: {
    indicatorStyle() {
      return {
        transform: `translate3d(-50%, ${Math.min(this.pull - 50, MAX_PULL - 50)}px, 0)`,
        opacity: Math.min(this.pull / TRIGGER_DISTANCE, 1),
      };
    },
    spinnerStyle() {
      if (this.refreshing) return {};
      const rotation = (this.pull / TRIGGER_DISTANCE) * 360;
      return { transform: `rotate(${rotation}deg)` };
    },
  },
  mounted() {
    if (!this.enabled) return;
    document.addEventListener('touchstart', this.onStart, { passive: true });
    document.addEventListener('touchmove', this.onMove, { passive: false });
    document.addEventListener('touchend', this.onEnd, { passive: true });
    document.addEventListener('touchcancel', this.onEnd, { passive: true });
  },
  beforeUnmount() {
    if (!this.enabled) return;
    document.removeEventListener('touchstart', this.onStart);
    document.removeEventListener('touchmove', this.onMove);
    document.removeEventListener('touchend', this.onEnd);
    document.removeEventListener('touchcancel', this.onEnd);
  },
  methods: {
    // Find the nearest scrollable ancestor. If it's scrolled down, we don't
    // arm — let the native scroll happen. Only arm when the page is at the
    // very top.
    findScrollable(el) {
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    },
    onStart(e) {
      if (this.refreshing) return;
      const touch = e.touches[0];
      const scrollable = this.findScrollable(e.target);
      const scrollTop = scrollable ? scrollable.scrollTop : (window.scrollY || document.documentElement.scrollTop);
      if (scrollTop > 0) {
        this.startScrollable = null;
        return;
      }
      this.startY = touch.clientY;
      this.startScrollable = scrollable;
      this.active = true;
      this.pull = 0;
    },
    onMove(e) {
      if (!this.active || this.refreshing) return;
      const dy = e.touches[0].clientY - this.startY;
      if (dy <= 0) {
        this.pull = 0;
        return;
      }
      // Only prevent the default scroll when we're clearly in a pull gesture,
      // otherwise we'd fight ordinary vertical scrolling.
      if (dy > 5) {
        e.preventDefault();
      }
      // Rubber-band: pull distance grows sub-linearly past the trigger point
      this.pull = dy < TRIGGER_DISTANCE
        ? dy
        : TRIGGER_DISTANCE + (dy - TRIGGER_DISTANCE) * 0.4;
    },
    onEnd() {
      if (!this.active || this.refreshing) return;
      if (this.pull >= TRIGGER_DISTANCE) {
        this.refreshing = true;
        this.pull = TRIGGER_DISTANCE;
        setTimeout(() => window.location.reload(), 350);
      } else {
        this.active = false;
        this.pull = 0;
      }
    },
  },
};
</script>

<style scoped>
.ptr-indicator {
  position: fixed;
  top: env(safe-area-inset-top);
  left: 50%;
  z-index: 2000;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  color: #555;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  transition: opacity 0.15s ease;
  will-change: transform, opacity;
}
.ptr-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
}
.ptr-refreshing .ptr-spinner {
  animation: ptr-spin 0.9s linear infinite;
}
@keyframes ptr-spin {
  to { transform: rotate(360deg); }
}
</style>
